import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    Status,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import {
    ApiFichiersPostExpediteurEnum,
    ApiFichiersPostExpeditionEnum,
    FichiersDossierJsonldDocumentfileRead,
    FichiersDossierCreatedFilesPostResponseDtoJsonldDocumentfileRead,
    TblTypeDocumentJsonldTypedocumentdossierRead,
    TypeDeDocumentDesDossiersApiApiTypesDocDossierPostRequest,
    TypeDeDocumentDesDossiersJsonldTypedocumentdossierCreate,
    TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDownload } from '@europrocurement/l2d-modules/hooks';

export type TypeDocument = TblTypeDocumentJsonldTypedocumentdossierRead;
export type DossierDocument = TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead;
export type DossierFichier = FichiersDossierJsonldDocumentfileRead;
export type DossierFichiers = FichiersDossierCreatedFilesPostResponseDtoJsonldDocumentfileRead;
export type DocumentColor = 'primary' | 'error' | 'success' | 'secondary' | 'warning';
export type DocumentExtension = 'pdf' | 'jpg' | 'jpeg' | 'png' | 'doc' | 'docx' | 'xls' | 'xlsx';
export type DocumentIconType =
    | 'file'
    | 'file-pdf'
    | 'file-jpg'
    | 'file-png'
    | 'file-doc'
    | 'file-xls';

const STATUS_TYPE_DOCUMENT = 2;
export const STATUS_VALID = 9;
export const STATUS_REFUSED = 10;
const TYPE_NO_TYPE = 66;
const TYPE_LIASSE = 118;
export const EXPEDITION_PRO = 1;
export const EXPEDITION_API = 5;
export const EXPEDITEUR_PRESCRIPTEUR = 1;
export const EXPEDITEUR_FORMALISTE = 3;

const errorStatuses: Array<number> = [STATUS_REFUSED];
const successStatuses: Array<number> = [STATUS_VALID];

const useDossierDocuments = () => {
    const { documentsApi, statusApi, fichiersDossierApi } = useFormaliteService();
    const { download } = useDownload();

    const getDossierDocuments = (dossierId: number) =>
        documentsApi
            .apiDossiersDossierTypesDocDossierGetCollection({
                dossier: dossierId as unknown as string,
                itemsPerPage: 100,
            })
            .then((res) => res.data['hydra:member'] as Array<DossierDocument>);

    /**
     * link a document type to a dossier
     * @param dossierId number
     * @param typeDocumentId number documentType id
     * @param libelle string documentType libelle
     * @param dossierStatusId number documentType id
     */
    const postDossierTypeDocuments = (
        dossierId: number,
        typeDocumentId: number,
        libelle: string,
        dossierStatusId: number,
    ) =>
        documentsApi
            .apiTypesDocDossierPost({
                typeDeDocumentDesDossiersJsonldTypedocumentdossierCreate: {
                    dossier: `/formalite/dossiers/${dossierId}`,
                    document: `/formalite/types-documents/${typeDocumentId}`,
                    libelle,
                    statut: `/formalite/statuts/${dossierStatusId}`,
                    noteFormaliste: '',
                    deleted: false,
                } as TypeDeDocumentDesDossiersJsonldTypedocumentdossierCreate,
            } as TypeDeDocumentDesDossiersApiApiTypesDocDossierPostRequest)
            .then((res) => res.data);

    const getDocumentStatuses = () =>
        statusApi
            .apiStatutsGetCollection({ type: STATUS_TYPE_DOCUMENT })
            .then((res) => res.data['hydra:member'] as Array<Status>);

    const getDossierFichiers = (dossierId: number) =>
        fichiersDossierApi
            .apiDossiersDossierFichiersGetCollection({
                dossier: dossierId as unknown as string,
                itemsPerPage: 100,
            })
            .then((res) => res.data['hydra:member'] as Array<DossierFichier>);

    const uploadDossierFichier = (
        dossierId: number,
        typeDoc: string,
        file: File,
        expediteur?: ApiFichiersPostExpediteurEnum,
        expedition?: ApiFichiersPostExpeditionEnum,
        typageAuto?: boolean,
        deliassageAuto?: boolean,
    ) =>
        fichiersDossierApi
            .apiFichiersPost({
                dossier: dossierId as unknown as string,
                typeDoc,
                file,
                expediteur: expediteur || EXPEDITEUR_PRESCRIPTEUR,
                expedition: expedition || EXPEDITION_API,
                typageAuto: typageAuto ?? false,
                deliassageAuto: deliassageAuto ?? true,
            })
            .then((res) => res.data as DossierFichiers);

    const uploadAdditionalDossierFichier = (
        dossierId: number,
        file: File,
        expediteur?: ApiFichiersPostExpediteurEnum,
        expedition?: ApiFichiersPostExpeditionEnum,
        typageAuto?: boolean,
        deliassageAuto?: boolean,
    ): Promise<DossierFichiers> =>
        fichiersDossierApi
            .apiFichiersPost({
                dossier: dossierId as unknown as string,
                file,
                expediteur: expediteur || EXPEDITEUR_PRESCRIPTEUR,
                expedition: expedition || EXPEDITION_API,
                typageAuto: typageAuto ?? true,
                deliassageAuto: deliassageAuto ?? true,
            })
            .then((res) => res.data as DossierFichiers);

    const removeDossierFichier = (fichierId: number) =>
        fichiersDossierApi.apiFichiersIdDelete({ id: fichierId as unknown as string });

    const updateDossierFichier = (fichierId: number, payload: Record<string, unknown>) => {
        const requestParameter = {
            id: fichierId as unknown as string,
            fichiersDossierUpdateDocumentFileDocumentfileUpdate: {
                typageAuto: false,
                ...payload,
            },
        };

        return fichiersDossierApi.apiFichiersIdPatch(requestParameter);
    };

    const getStatusIdentifier = (statusId: number): 'pending' | 'error' | 'success' => {
        if (errorStatuses.includes(statusId)) {
            return 'error';
        }

        if (successStatuses.includes(statusId)) {
            return 'success';
        }

        return 'pending';
    };

    const getColorByStatus = (status: 'pending' | 'error' | 'success'): DocumentColor => {
        if (status === 'error') {
            return 'error';
        }

        if (status === 'success') {
            return 'success';
        }

        return 'warning';
    };

    const getFichierFromDocumentId = (
        documentId: number,
        fichiers: Array<DossierFichier>,
    ): DossierFichier | undefined =>
        fichiers.find((f) => Number(f.typeDocDossier?.id) === documentId);

    const getFichierFromDocument = (
        document: DossierDocument,
        fichiers: Array<DossierFichier>,
    ): DossierFichier | undefined => getFichierFromDocumentId(Number(document?.id), fichiers);

    const documentHasFichier = (
        document: DossierDocument,
        fichiers: Array<DossierFichier>,
    ): DossierFichier | undefined =>
        fichiers.find((f) => Number(f.typeDocDossier?.id) === Number(document?.id));

    const getFichierContent = (fichierId: number) =>
        fichiersDossierApi.apiFichiersIdFileGet(
            { id: fichierId as unknown as string },
            { responseType: 'blob' },
        );

    const downloadFichier = async (fichier: DossierFichier) => {
        const content = await getFichierContent(fichier.id as number);
        download(fichier.fichier as string, content.data as unknown as Blob);
    };

    const downloadFilesAsArchive = async (
        fileName: string,
        dossierId: number,
        fileTypes: number[],
    ) => {
        const content = await fichiersDossierApi.apiDossiersDossierFichiersGetCollection(
            {
                dossier: dossierId as unknown as string,
                expediteur2: fileTypes,
            },
            {
                responseType: 'arraybuffer',
                headers: {
                    accept: 'application/zip',
                },
            },
        );

        download(fileName, content.data as unknown as Blob, 'application/zip');
    };

    const isClientDocumentType = (type: number) => type === DOC_TYPE_REQUIRED;

    const isFormalisteDocumentType = (type: number) => type === DOC_TYPE_DOWNLOADABLE;

    const isIntenalDocumentType = (type: number) => type === DOC_TYPE_INTERNAL;

    const isNoType = (documentTypeId: number) =>
        [TYPE_LIASSE, TYPE_NO_TYPE].includes(documentTypeId);

    const isFileSupported = (extension: DocumentExtension): boolean =>
        ['pdf', 'jpg', 'png'].includes(extension);

    const getFileIcon = (extension: DocumentExtension): DocumentIconType => {
        switch (extension) {
            case 'pdf':
                return 'file-pdf';
            case 'jpg':
            case 'jpeg':
                return 'file-jpg';
            case 'png':
                return 'file-png';
            case 'doc':
            case 'docx':
                return 'file-doc';
            case 'xls':
            case 'xlsx':
                return 'file-xls';
            default:
                return 'file';
        }
    };

    return {
        getDossierDocuments,
        getDossierFichiers,
        getDocumentStatuses,
        uploadDossierFichier,
        uploadAdditionalDossierFichier,
        removeDossierFichier,
        updateDossierFichier,
        getStatusIdentifier,
        getColorByStatus,
        getFichierFromDocument,
        getFichierFromDocumentId,
        documentHasFichier,
        downloadFichier,
        downloadFilesAsArchive,
        getFichierContent,
        isClientDocumentType,
        isFormalisteDocumentType,
        isIntenalDocumentType,
        isNoType,
        isFileSupported,
        getFileIcon,
        postDossierTypeDocuments,
    };
};

export default useDossierDocuments;
